
import { usePinia, useSearchStore, useSearchWizardStore } from '#imports'
import { Component, Prop, Vue, Watch } from 'vue-property-decorator'
import { HierarchyTree } from '~/apps/hierarchies/domain/types'
import { getHierarchyTree } from '~/apps/hierarchies/inversify.config'
import {
  amountMinorToAmount,
  amountToAmountMinor,
} from '~/utility/amount-minor'
import { getMetaFromRoute } from '~/utility/route'
import { isCampsiteDetailsRoute } from '../../../pu-links/domain/Route'
import { SearchLinkGenerator } from '../../../url/generators/SearchLinkGenerator'
import { isPointSearch } from '../../domain/isPointSearch'
import { SearchFilters } from '../../schemas'
import { PriceRange } from '../../types'

@Component({
  setup() {
    const searchStore = useSearchStore(usePinia())
    const searchWizardStore = useSearchWizardStore(usePinia())
    return {
      searchWizardStore,
      searchStore,
    }
  },
})
export default class FilterAndSortMain extends Vue {
  searchWizardStore: ReturnType<typeof useSearchWizardStore>
  searchStore: ReturnType<typeof useSearchStore>

  @Prop()
    placename?: string

  hierarchyTree: HierarchyTree | null = null

  @Prop()
    alreadyOpened: boolean

  @Watch('alreadyOpened')
  async onAlreadyOpenedChanged(val: boolean, oldVal: boolean) {
    if (val && !oldVal) {
      await getHierarchyTree(
        this.hierarchyPath || '/',
        this.$route.params.lang || 'en-gb',
      ).then(({ hierarchyTree }) => {
        this.hierarchyTree = hierarchyTree
      })
    }
  }

  updatedSearchFilters: SearchFilters | null = null

  maxPriceLoaded = true

  private currentPriceRange: SearchFilters['priceRange'] | null = null

  created() {
    this.setPriceRangeValues()
  }

  activated() {
    this.setPriceRangeValues()
  }

  @Watch('updatedSearchFilters', { deep: true })
  async updatedSearchFiltersUpdated() {
    if (this.searchWizardStore.panelOpen && this.updatedSearchFilters) {
      await Promise.all([
        this.searchWizardStore.setWizardFilters(this.updatedSearchFilters),
        this.search(),
      ])
    }
  }

  @Watch('searchWizardStore.panelOpen')
  async onPanelOpenChanged(val: boolean) {
    (this.$refs['scrolling-contents'] as HTMLElement)?.scrollTo(0, 0)
    if (val && this.updatedSearchFilters) {
      await this.search()
      this.setPriceRangeValues()
    }
  }

  // when the wizard filters change, update our local copy
  // do we need to do this? could the filter and sort just use wizard filters directly?
  @Watch('searchWizardStore.wizardFilters', { deep: true, immediate: true })
  wizardFiltersUpdated(newFilters: SearchFilters) {
    // we copy the filters so we can modify them without affecting the wizard
    this.updatedSearchFilters = { ...newFilters }
  }

  @Watch('searchStore.searchFilters', { deep: true })
  searchFiltersUpdated(newFilters) {
    // we copy the filters so we can modify them without affecting the wizard
    this.updatedSearchFilters = { ...newFilters }
  }

  setPriceRangeValues() {
    this.currentPriceRange =
      this.searchStore?.countSearchFilters?.priceRange || null
  }

  async search() {
    if (isCampsiteDetailsRoute(this.$route)) {
      return
    }
    // TODO: move hierarchy tree
    await getHierarchyTree(
      this.hierarchyPath || '/',
      this.$route.params.lang || 'en-gb',
    ).then(({ hierarchyTree }) => {
      this.hierarchyTree = hierarchyTree
    })
  }

  updateHierarchy(path: string) {
    if (this.updatedSearchFilters) {
      Vue.set(
        this.updatedSearchFilters,
        'hierarchyPath',
        path === '/' ? undefined : path,
      )
    }
  }

  get hierarchyPath() {
    return this.updatedSearchFilters?.hierarchyPath || '/'
  }

  get shouldShowLocationFilter() {
    return (
      !this.updatedSearchFilters?.area ||
      (this.updatedSearchFilters?.area &&
        this.updatedSearchFilters?.area.isEmptyPoint)
    )
  }

  get shouldShowSortOptions() {
    return this.targetUrl !== 'maps'
  }

  get sortOptions() {
    if (this.searchStore.context?.results?.sortOptions) {
      return this.searchStore.context.results.sortOptions
    }
    return []
  }

  get selectedCategoryIds() {
    return this.updatedSearchFilters?.categoryIds
      ? this.updatedSearchFilters?.categoryIds
      : []
  }

  get selectedFacets() {
    return this.updatedSearchFilters?.facets
      ? this.updatedSearchFilters?.facets
      : []
  }

  get isBoundsSearch(): boolean {
    return !!this.updatedSearchFilters?.bounds
  }

  updateSelectedCategories(categoryIds: number[]) {
    if (this.updatedSearchFilters) {
      this.updatedSearchFilters.categoryIds = categoryIds.map((id) =>
        id.toString(),
      )
    }
  }

  updateSelectedFacets(slugs: string[]) {
    if (this.updatedSearchFilters) {
      Vue.set(this.updatedSearchFilters, 'facets', slugs)
    }
  }

  sortOption = ''

  @Watch('searchStore.context.results.sortOption', { immediate: true })
  setInitialSortOption(sort: string) {
    this.sortOption = sort
  }

  updateSort(sort: string) {
    this.sortOption = sort
  }

  updateRadius(within: number) {
    this.searchWizardStore.send('SET_WITHIN', { within })
    // eslint-disable-next-line no-console
    this.searchWizardStore.updateCounts().catch((e) => console.warn(e))
  }

  get radius() {
    return (
      this.searchWizardStore?.within ||
      this.searchWizardStore?.wizardFilters?.within ||
      this.searchWizardStore?.wizardFilters?.area?.rangeMiles ||
      this.searchStore?.context?.results?.within?.mi ||
      this.$config.public.defaultWithinRange
    )
  }

  get isPriceSearch() {
    return !!this.updatedSearchFilters?.dates
  }

  get searchMaxPrice() {
    const searchMaxPrice = this.searchStore?.searchCounts?.resultMaxPrice
    const userMaxPrice = this.updatedSearchFilters?.priceRange?.max
    const originalMaxPrice = this.searchStore?.searchFilters?.priceRange?.max
    const max =
      [searchMaxPrice, userMaxPrice, originalMaxPrice].sort((a, b) => {
        const y = a === undefined ? 0 : a
        const z = b === undefined ? 0 : b
        return z - y
      })[0] || 0
    return amountToAmountMinor(max)
  }

  get selectedPriceRange() {
    const range = this.currentPriceRange
    return range
      ? [
        range.min ? amountToAmountMinor(range.min) : 0,
        range.max
          ? isFinite(range.max)
            ? amountToAmountMinor(range.max)
            : this.searchMaxPrice
          : this.searchMaxPrice,
      ]
      : undefined
  }

  updatePriceRange({ manual, range }: { range: PriceRange; manual: boolean }) {
    const newRange = {
      min: range.min === 0 ? 0 : amountMinorToAmount(range.min),
      max: range.max === Infinity ? 0 : amountMinorToAmount(range.max),
    }
    if (this.updatedSearchFilters) {
      Vue.set(this.updatedSearchFilters, 'priceRange', newRange)
    }
    if (manual) {
      Vue.set(this, 'currentPriceRange', newRange)
    }
  }

  async goToSearchPage() {
    const { path, query } = this.getSearchPageUrlData()
    await this.$router.push(
      this.$puLocalePath({
        path,
        query: query as { [key: string]: string },
      }),
    )
    this.$emit('filtersSubmit')
  }

  private getSearchPageUrlData() {
    const filters = this.getSearchPageFilters()
    const input = {
      category: this.targetUrl,
      customQueryParams: this.placename
        ? { placename: this.placename }
        : undefined,
      filters: { ...filters, page: undefined },
      hierarchyPath: this.updatedSearchFilters?.hierarchyPath,
    }
    return new SearchLinkGenerator().getUrlData(input)
  }

  get targetUrl() {
    const meta = getMetaFromRoute(this.$route)
    return (meta.type && meta.type === 'maps') ||
      this.searchStore?.searchFilters?.base === 'maps'
      ? 'maps'
      : 'search'
  }

  private getSearchPageFilters(): SearchFilters {
    const bounds = this.updatedSearchFilters?.bounds
    return {
      ...this.updatedSearchFilters,
      area: this.areaParamForSearchPageFilters,
      hierarchyPath: undefined,
      bounds: this.updatedSearchFilters?.hierarchyPath ? undefined : bounds,
      sort: this.sortingParamForSearchPageFilters,
    }
  }

  private get areaParamForSearchPageFilters() {
    return this.updatedSearchFilters?.area &&
      isPointSearch(this.updatedSearchFilters)
      ? this.updatedSearchFilters.area
      : undefined
  }

  private get sortingParamForSearchPageFilters() {
    return this.updatedSearchFilters &&
      !isPointSearch(this.updatedSearchFilters) &&
      this.sortOption === 'distance'
      ? ''
      : this.sortOption
  }

  get isPriceRangeSearch(): boolean {
    const priceRange = this.updatedSearchFilters?.priceRange || undefined
    return !!(priceRange?.min && priceRange?.max && priceRange.max !== Infinity)
  }

  async onFooterCancel() {
    await this.searchWizardStore.resetToPreviousFilters()
    this.$emit('cancel')
  }

  removedFacet = ''
  removeFacet(facet: string): void {
    if (!this.updatedSearchFilters || !this.updatedSearchFilters?.facets) {
      throw new Error('Cannot find updatedSearchFilters')
    }
    this.updatedSearchFilters?.facets.splice(
      this.updatedSearchFilters?.facets.indexOf(facet),
      1,
    )
    this.updatedSearchFilters.facets = this.updatedSearchFilters?.facets.slice()
    this.removedFacet = facet
  }

  removeBounds() {
    if (this.updatedSearchFilters) {
      Vue.set(this.updatedSearchFilters, 'bounds', undefined)
    }
  }

  removePriceRange() {
    this.updatePriceRange({
      range: { min: 0, max: Infinity },
      manual: false,
    })
  }
}
